import CryptoJS from 'crypto-js';

import Configs from '@/Configs';
import { canonicalizeCompanyId } from '@/common/canonicalizeCompanyId';
import type { ContextProfile } from '@/common/dataContext';
import { queriesKeys } from '@/queries/queries-keys';
import { platformQueryClient } from '@/queries/query-client';

/** Same numeric coercion as company ids — employee ids must not diverge on `"12"` vs `12`. */
export const canonicalizeEmployeeId = canonicalizeCompanyId;

/**
 * Resolve cache key ids from auth context and/or a profile payload
 * (login often writes profile in the same tick as setCompanyID / setEmployeeID).
 *
 * @param {string | number | null | undefined} companyID
 * @param {string | number | null | undefined} employeeID
 * @param {ContextProfile | null | undefined} value
 * @returns {{ companyID: number | null, employeeID: number | null }}
 */
export function resolveSessionProfileCacheIds(
  companyID: string | number | null | undefined,
  employeeID: string | number | null | undefined,
  value?: ContextProfile | null
): { companyID: number | null; employeeID: number | null } {
  const resolvedCompanyID =
    canonicalizeCompanyId(companyID) ??
    canonicalizeCompanyId(value?.companyID) ??
    canonicalizeCompanyId(value?.employeeInfo?.companyID) ??
    canonicalizeCompanyId(value?.companyInfo?.companyID);

  const resolvedEmployeeID =
    canonicalizeEmployeeId(employeeID) ??
    canonicalizeEmployeeId(value?.employeeID) ??
    canonicalizeEmployeeId(value?.employeeInfo?.employeeID);

  return { companyID: resolvedCompanyID, employeeID: resolvedEmployeeID };
}

/**
 * Read decrypted session self-profile (full `employeeProfile`, else legacy `employeeInfo` wrap).
 *
 * @returns {ContextProfile | null}
 */
export function readSessionProfileFromSession(): ContextProfile | null {
  try {
    const fullStored = sessionStorage.getItem('employeeProfile');
    if (fullStored) {
      const full = JSON.parse(
        CryptoJS.AES.decrypt(fullStored, Configs.privateKey).toString(CryptoJS.enc.Utf8)
      );
      if (full && typeof full === 'object') {
        return full as ContextProfile;
      }
    }
  } catch (error) {
    console.error('[sessionProfileCache] Failed to parse employeeProfile from sessionStorage', error);
  }

  try {
    const storedData = sessionStorage.getItem('employeeInfo');
    if (!storedData) {
      return null;
    }
    const employeeInfo = JSON.parse(
      CryptoJS.AES.decrypt(storedData, Configs.privateKey).toString(CryptoJS.enc.Utf8)
    );
    // Legacy sessions stored employeeInfo only — wrap so Self can use employeeInfo.* paths.
    if (employeeInfo?.employeeID != null && !employeeInfo?.employeeInfo) {
      return { employeeInfo } as ContextProfile;
    }
    return employeeInfo as ContextProfile;
  } catch (error) {
    console.error('[sessionProfileCache] Failed to parse employeeInfo from sessionStorage', error);
    return null;
  }
}

/**
 * Persist the session key bundle historically written by `getProfile` (self path).
 * Safe to call from a Query `queryFn` without touching the Query cache.
 *
 * @param {ContextProfile | null} value
 */
export function persistSessionProfileKeys(value: ContextProfile | null): void {
  try {
    if (!value) {
      sessionStorage.removeItem('employeeInfo');
      sessionStorage.removeItem('employeeProfile');
      sessionStorage.removeItem('managerInfo');
      sessionStorage.removeItem('companyInfo');
      sessionStorage.removeItem('securityLevel');
      return;
    }

    if (value.employeeInfo) {
      sessionStorage.setItem(
        'employeeInfo',
        CryptoJS.AES.encrypt(JSON.stringify(value.employeeInfo), Configs.privateKey).toString()
      );
    }
    sessionStorage.setItem(
      'employeeProfile',
      CryptoJS.AES.encrypt(JSON.stringify(value), Configs.privateKey).toString()
    );
    if (value.managerInfo) {
      sessionStorage.setItem(
        'managerInfo',
        CryptoJS.AES.encrypt(JSON.stringify(value.managerInfo), Configs.privateKey).toString()
      );
    }
    if (value.companyInfo) {
      sessionStorage.setItem(
        'companyInfo',
        CryptoJS.AES.encrypt(JSON.stringify(value.companyInfo), Configs.privateKey).toString()
      );
    }
    if (value.roles?.securityLevels) {
      sessionStorage.setItem(
        'securityLevel',
        CryptoJS.AES.encrypt(
          JSON.stringify(value.roles.securityLevels),
          Configs.privateKey
        ).toString()
      );
    }
  } catch (error) {
    console.error('[sessionProfileCache] Failed to write session profile keys', error);
  }
}

/**
 * Persist self-profile to TanStack cache + encrypted sessionStorage (legacy readers).
 * Pass `null` to clear both for the resolved key.
 *
 * @param {string | number | null | undefined} companyID
 * @param {string | number | null | undefined} employeeID
 * @param {ContextProfile | null} value
 */
export function writeSessionProfileCache(
  companyID: string | number | null | undefined,
  employeeID: string | number | null | undefined,
  value: ContextProfile | null
): void {
  const ids = resolveSessionProfileCacheIds(companyID, employeeID, value);
  if (ids.companyID != null && ids.employeeID != null) {
    const key = queriesKeys.sessionSelfProfile(ids.companyID, ids.employeeID);
    if (value == null) {
      platformQueryClient.removeQueries({ queryKey: key });
    } else {
      platformQueryClient.setQueryData(key, value);
    }
  }

  persistSessionProfileKeys(value);
}
